// The web app's root (E12-S1-T1): TanStack Query, the API client and the session state are provided
// here, then the frame with the routes for whoever is signed in.
//
// Wiring that matters: the client's onUnauthorised records the current path in the session store,
// so a 401 from any request — the session check or a page's query — flips the whole app to
// signed-out and keeps where the user was for the return. Signed out, the guard draws the sign-in
// page on every route and no page mounts (src/auth/guard.tsx). The theme gate applies the tenant's
// theme before the first frame is shown (src/theme/theme-gate.tsx).
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { createApiClient } from "./api/client.js";
import { isRetryable } from "./api/errors.js";
import { ApiProvider } from "./api/provider.js";
import { SessionGuard } from "./auth/guard.js";
import {
  createSessionStore,
  currentPath,
  SessionProvider,
  type SessionStore,
} from "./auth/session-state.js";
import { AppRoutes } from "./routes.js";
import { ThemeGate } from "./theme/theme-gate.js";
import type { ReportTheme } from "./theme/tenant-theme.js";

export interface AppProps {
  /** The fetch the API client uses; the tests pass a stub API. Defaults to the global fetch. */
  readonly fetch?: typeof fetch;
  readonly session?: SessionStore;
  readonly queryClient?: QueryClient;
  /** Where the tenant's theme is applied (E12-S1-T4). Defaults to this document's <html>. */
  readonly themeRoot?: HTMLElement;
  /** Where theme problems are reported. Defaults to a console warning. */
  readonly onThemeReport?: ReportTheme;
}

/** Ask again only when the network or the server may recover, twice at most. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: (failures, err) => isRetryable(err) && failures < 2 },
      mutations: { retry: false },
    },
  });
}

export function App(props: AppProps) {
  const [deps] = useState(() => {
    const session = props.session ?? createSessionStore();
    const client = createApiClient({
      ...(props.fetch === undefined ? {} : { fetch: props.fetch }),
      onUnauthorised: () => {
        session.signOut(currentPath());
      },
    });
    return { session, client, queryClient: props.queryClient ?? createQueryClient() };
  });
  return (
    <QueryClientProvider client={deps.queryClient}>
      <ApiProvider client={deps.client}>
        <SessionProvider store={deps.session}>
          <ThemeGate root={props.themeRoot} report={props.onThemeReport}>
            <SessionGuard>{(principal) => <AppRoutes principal={principal} />}</SessionGuard>
          </ThemeGate>
        </SessionProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
